/* eslint-disable camelcase */
import React, { useCallback, useEffect, useRef } from 'react';
import FrameworkUtils from 'utils/framework';
import { Flex, Text } from 'components/ui';
import getResource from 'ai/components/SuperChat/locales/SuperChat_en_US';
import PromptCategory from 'ai/components/SuperChat/WelcomeMat/PromptCategory';
import { setWelcomeMat } from 'ai/utils/sessionStorage';
import { mediaQueries } from 'style/config';
import { parsePromptContent } from 'ai/utils/aiBeautyChat';
import { trackWelcomeMatPromptClick, trackWelcomeMatImpression } from 'ai/analytics';

const { wrapFunctionalComponent } = FrameworkUtils;

function WelcomeMat(props) {
    const { sendMessage, isDisabled = false, config, onPromptClick } = props;
    const welcomeMatRef = useRef(null);
    const hasBeenSeen = useRef(false);

    const categories = config?.category_cards
        ? config.category_cards.map(category => ({
            title: category.title,
            prompts: category.prompts.map(prompt => parsePromptContent(prompt, getResource('new')))
        }))
        : [];

    useEffect(() => {
        if (!categories.length || hasBeenSeen.current || !welcomeMatRef.current) {
            return undefined;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && !hasBeenSeen.current) {
                    hasBeenSeen.current = true;
                    trackWelcomeMatImpression();
                    setWelcomeMat();
                    observer.disconnect();
                }
            },
            { threshold: 0.5 }
        );

        observer.observe(welcomeMatRef.current);

        return () => observer.disconnect();
    }, [categories.length]);

    const handlePromptClick = useCallback(
        (promptText, trackingData) => {
            setWelcomeMat();

            if (trackingData) {
                trackWelcomeMatPromptClick(trackingData);
            }

            if (onPromptClick) {
                onPromptClick();
            }

            if (sendMessage) {
                sendMessage(promptText);
            }
        },
        [sendMessage, onPromptClick]
    );

    if (!categories.length) {
        return null;
    }

    return (
        <Flex
            ref={welcomeMatRef}
            width='100%'
            flexDirection='column'
            gap={4}
        >
            <Text
                marginBottom={3}
                fontSize='md'
                textAlign='center'
                lineHeight='tight'
            >
                {config?.meta?.modal_value_prop || getResource('description')}
            </Text>

            <Flex
                css={styles.categoriesContainer}
                gap={[3, 4]}
                paddingX={[4, 0]}
                marginX={[-4, 0]}
            >
                {categories.map((category, index) => (
                    <PromptCategory
                        key={index}
                        title={category.title}
                        prompts={category.prompts}
                        onPromptClick={handlePromptClick}
                        isDisabled={isDisabled}
                    />
                ))}
            </Flex>
        </Flex>
    );
}

const styles = {
    categoriesContainer: {
        flexDirection: 'column',
        [mediaQueries.smMax]: {
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
            overflowX: 'auto',
            flexDirection: 'row'
        }
    }
};

export default wrapFunctionalComponent(WelcomeMat, 'WelcomeMat');
